import { Injectable, inject } from '@angular/core';
import { Commands, Resources, Search, Experiences } from '@local/client-contracts';
import { observable } from '@local/common';
import { ResourcesRpcInvoker } from '@local/common-web';
import { Logger } from '@unleash-tech/js-logger';
import { firstValueFrom, ReplaySubject, type Observable } from 'rxjs';
import { ApplicationsService } from './applications.service';
import { LogService } from './log.service';
import { ServicesRpcService } from './rpc.service';
import { ExperiencesService } from 'src/app/bar/services/experiences.service';

export interface AppResource {
  title: string;
  service: string;
  app: string;
}

export interface ExperienceDataContent extends Experiences.ExperienceItem {
  createdByTooltip?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ResourcesService implements Resources.Service {
  service: Resources.Service;
  private logger: Logger;
  private assistantService = inject(ExperiencesService);
  private _typeMap$: ReplaySubject<{ [type: string]: AppResource }> = new ReplaySubject(1);
  private _linksCount$: ReplaySubject<Resources.ListLinkStatsResponseItem[]> = new ReplaySubject(1);

  @observable
  get typeMap$() {
    return this._typeMap$.asObservable();
  }

  @observable
  get linksCount$(): Observable<Resources.ListLinkStatsResponseItem[]> {
    return this._linksCount$.asObservable();
  }

  constructor(
    logService: LogService,
    private services: ServicesRpcService,
    private apps: ApplicationsService
  ) {
    this.logger = logService.scope(this.constructor.name);
    this.service = this.services.invokeWith(ResourcesRpcInvoker, 'resources');

    this.apps.all$.subscribe((apps) => {
      const res: { [type: string]: AppResource } = {};

      for (const app of apps) {
        for (const r of app.resources) {
          res[r.type] = { title: r.title, app: app.name, service: r.service };
        }
      }

      this._typeMap$.next(res);
    });

    this.service.linksCount$.subscribe((links) => {
      this._linksCount$.next(links);
    });
  }

  refreshLinkCounts(links: Resources.ListLinkStatsRequestItem[]): void {
    this.service.refreshLinkCounts(links);
  }

  async get(type: string): Promise<AppResource> {
    return (await firstValueFrom(this.typeMap$))[type];
  }

  async openWith(id: string): Promise<Commands.OpenWith> {
    return this.service.openWith(id);
  }

  listShareItems(url: string, resourceId?: string, resourceTitle?: string): Promise<Resources.ShareItem[]> {
    return this.service.listShareItems(url, resourceId, resourceTitle);
  }

  feedback(request: Resources.FeedbackRequest): Promise<void> {
    return this.service.feedback(request);
  }

  getBulkByExternalId(externalIds: string[], cache: Resources.CacheMode, origin: Search.Origin): Promise<Resources.ResourceEntry[]> {
    return this.service.getBulkByExternalId(externalIds, cache, origin);
  }

  searchFilters(request: Resources.SearchFiltersRequest): Promise<Resources.SearchFiltersResponse> {
    throw new Error('Method not implemented.');
  }

  getFilterStats(request: Resources.FilterStatsRequest): Promise<Resources.FilterStatsResponse> {
    throw new Error('Method not implemented.');
  }

  getAssistantsByLink(experiences: any): { [linkId: string]: ExperienceDataContent[] } {
    if (!experiences) {
      return {};
    }

    const assistantsByLink: { [linkId: string]: ExperienceDataContent[] } = {};

    experiences.forEach((experience: Experiences.ExperienceItem) => {
      if (experience.dataSources && Array.isArray(experience.dataSources)) {
        experience.dataSources.forEach((dataSource) => {
          if (dataSource.items && Array.isArray(dataSource.items)) {
            dataSource.items.forEach((item) => {
              const linkId = item.filter?.linkId;
              if (linkId) {
                if (!assistantsByLink[linkId]) {
                  assistantsByLink[linkId] = [];
                }
                const existingAssistant = assistantsByLink[linkId].find((existing) => existing.id === experience.id);
                if (!existingAssistant) {
                  const phrase = this.assistantService.getAssistantTimePhrases(experience);
                  const createdBy = `By ${phrase.startModifiedPhrase}`;
                  const tooltip = `${phrase.preDisplayModifiedTime}by ${phrase.startModifiedPhrase} on ${phrase.fullDetails}`;

                  const experienceData: ExperienceDataContent = {
                    ...experience,
                    createdBy: createdBy,
                    createdByTooltip: tooltip,
                  };
                  assistantsByLink[linkId].push(experienceData);
                }
              }
            });
          }
        });
      }
    });

    return assistantsByLink;
  }
}
